// In-flight request de-duplication, applied at the axios adapter level.
//
// Rapid double / triple clicks on a submit button fire the same request many
// times before the first response lands. Across the app this was creating
// duplicate leads and duplicate entries. While a request with an identical
// signature (method + full URL + body) is still in flight, every further caller
// shares that one HTTP call instead of sending a new one. The key is removed as
// soon as the request settles, so a genuine re-submit after completion still
// works normally.
//
// Living in the adapter (not in individual thunks) means getData,
// fetchBankMaster, dynamicTokenPostJson and direct axiosInstance/axios calls are
// all covered without each call site having to opt in.

import axios, { AxiosError } from "axios";

const inFlight = new Map();

const isBinary = (value) =>
  (typeof File !== "undefined" && value instanceof File) ||
  (typeof Blob !== "undefined" && value instanceof Blob);

// A double-click re-sends the very same File objects from component state, so
// name/size/type/lastModified is enough to recognise the duplicate upload.
const binaryKey = (value) =>
  `[blob:${value.name || ""}:${value.size}:${value.type}:${value.lastModified || ""}]`;

// By the time the adapter runs, transformRequest has already turned plain
// objects into a JSON string; FormData / URLSearchParams / Blob pass through.
const bodyKey = (data) => {
  if (data == null) return "";
  if (typeof data === "string") return data;
  if (typeof URLSearchParams !== "undefined" && data instanceof URLSearchParams) {
    return data.toString();
  }
  if (typeof FormData !== "undefined" && data instanceof FormData) {
    const parts = [];
    for (const [name, value] of data.entries()) {
      parts.push(`${name}=${typeof value === "string" ? value : binaryKey(value)}`);
    }
    return parts.join("&");
  }
  if (isBinary(data)) return binaryKey(data);
  // ArrayBuffer, streams etc. can't be compared reliably — don't dedupe them.
  return null;
};

const requestKey = (config) => {
  const body = bodyKey(config.data);
  if (body === null) return null;
  return `${(config.method || "get").toUpperCase()} ${axios.getUri(config)}\n${body}`;
};

// Wraps an axios adapter. `shouldDedupe(config)` lets a shared instance (the
// global axios) limit this to our own API and leave third-party calls alone.
export const dedupeAdapter = (adapter, shouldDedupe = () => true) => (config) => {
  // A caller with its own AbortSignal could cancel the shared call for everyone.
  const key = config.signal || !shouldDedupe(config) ? null : requestKey(config);
  if (!key) return adapter(config);

  const shared = inFlight.get(key);
  if (!shared) {
    const request = Promise.resolve()
      .then(() => adapter(config))
      .finally(() => inFlight.delete(key));
    inFlight.set(key, request);
    return request;
  }

  // Duplicate caller: hand back its own response/error object bound to its own
  // config, so per-request bookkeeping (e.g. the 401-retry `_retry` flag that
  // AxiosInstance reads from error.config) isn't shared with the first caller.
  return shared.then(
    (response) => ({ ...response, config }),
    (error) => {
      throw error && error.isAxiosError
        ? AxiosError.from(error, error.code, config, error.request, error.response)
        : error;
    }
  );
};
